import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel,
  Badge, Box, Button, Container, Flex, Heading, HStack, Icon, Image, Link, List, ListIcon, ListItem,
  SimpleGrid, Spinner, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, VStack,
} from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import { Navigate, useLocation } from 'react-router-dom';
import { FiArrowRight, FiCheck, FiMic } from 'react-icons/fi';

export const COMPARE_SLUGS = [
  'cents-alternative',
  'curbside-laundries-alternative',
  'laundromat-pos-software',
  'wdf-pos',
  'llm-info',
  'wash-and-fold-pos',
  'laundry-pickup-delivery-software',
  'open-source-laundromat-pos',
];

const PRODUCT_HOST = /smartlaundrybasket|localhost|127\.0\.0\.1|onrender\.com/;

function readEmbedded(slug) {
  try {
    const el = document.getElementById('compare-page-data');
    const data = el ? JSON.parse(el.textContent) : null;
    return data && data.slug === slug ? data : null;
  } catch (e) {
    return null;
  }
}

export default function ComparePage() {
  const slug = useLocation().pathname.split('/')[1];
  const [page, setPage] = useState(() => readEmbedded(slug));
  const [failed, setFailed] = useState(false);
  const isProductHost = PRODUCT_HOST.test(window.location.hostname.toLowerCase());

  useEffect(() => {
    if (!isProductHost || (page && page.slug === slug)) return;
    let active = true;
    const base = process.env.REACT_APP_AWS_API_URL || '';
    axios.get(`${base}/api/compare-pages/${slug}`)
      .then(({ data }) => { if (active) setPage(data); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [slug, page, isProductHost]);

  if (!isProductHost || failed) return <Navigate to="/" replace />;
  if (!page) {
    return <Flex minH="60vh" align="center" justify="center"><Spinner size="lg" color="blue.500" /></Flex>;
  }

  const { comparison, press } = page;

  return (
    <Box bg="white" minH="100vh">
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.description} />
        <link rel="canonical" href={page.url} />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.description} />
        <meta property="og:url" content={page.url} />
      </Helmet>

      <Box as="nav" position="sticky" top={0} zIndex={100} bg="white" borderBottom="1px solid" borderColor="gray.100" boxShadow="sm">
        <Container maxW="1200px">
          <Flex py={3} align="center" justify="space-between">
            <Link href="/slb"><Image src="/slb-logo.png" alt="Smart Laundry Basket" h={{ base: '40px', md: '52px' }} objectFit="contain" /></Link>
            <HStack spacing={4}>
              <Text as="a" href="/slb#pricing" fontSize="sm" color="gray.600" display={{ base: 'none', md: 'block' }} _hover={{ color: 'blue.500' }}>Pricing</Text>
              <Button as="a" href="/quickstart" size="sm" colorScheme="blue" borderRadius="full" px={6}>Try It Free</Button>
            </HStack>
          </Flex>
        </Container>
      </Box>

      <Box bgGradient="linear(to-b, blue.50, white)" py={{ base: 14, md: 20 }}>
        <Container maxW="860px" textAlign="center">
          <VStack spacing={5}>
            <Badge colorScheme="blue" px={3} py={1} borderRadius="full">{page.badge}</Badge>
            <Heading as="h1" fontSize={{ base: '3xl', md: '5xl' }} lineHeight="shorter" fontWeight="800">{page.h1}</Heading>
            <Text fontSize={{ base: 'md', md: 'xl' }} color="gray.600" lineHeight="tall">{page.intro}</Text>
            <HStack spacing={4} flexWrap="wrap" justify="center" pt={2}>
              <Button as="a" href="/quickstart" size="lg" colorScheme="blue" borderRadius="full" px={8} rightIcon={<FiArrowRight />}>
                Set Up My Laundry Free
              </Button>
              <Button as="a" href="/slb#tour" size="lg" variant="outline" colorScheme="blue" borderRadius="full" px={8}>
                See the Product Tour
              </Button>
            </HStack>
            <Text fontSize="sm" color="gray.500">Free under $1,000/month revenue · capped at $149/month · no contract</Text>
          </VStack>
        </Container>
      </Box>

      {comparison && (
        <Box as="section" py={{ base: 10, md: 14 }}>
          <Container maxW="1000px">
            <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} mb={5} textAlign="center">{comparison.caption}</Heading>
            <TableContainer border="1px solid" borderColor="gray.200" borderRadius="xl" whiteSpace="normal">
              <Table size="md">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>{comparison.labelHeader || ''}</Th>
                    {comparison.columns.map((c, i) => (
                      <Th key={c} color={i === 0 ? 'blue.600' : undefined}>{c}</Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {comparison.rows.map((row) => (
                    <Tr key={row[0]}>
                      {row.map((cell, i) => (
                        <Td key={i} fontWeight={i === 0 ? '600' : undefined} fontSize="sm"
                          bg={i === 1 ? 'blue.50' : undefined} verticalAlign="top">
                          {cell}
                        </Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            {comparison.note && <Text fontSize="xs" color="gray.500" mt={3}>{comparison.note}</Text>}
          </Container>
        </Box>
      )}

      <Box as="section" py={{ base: 8, md: 12 }} bg="gray.50">
        <Container maxW="900px">
          <VStack spacing={10} align="stretch">
            {page.sections.map((s) => (
              <Box key={s.heading}>
                <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} mb={3}>{s.heading}</Heading>
                {s.body && <Text color="gray.700" lineHeight="tall" mb={s.bullets ? 3 : 0}>{s.body}</Text>}
                {s.bullets && (
                  <List spacing={2}>
                    {s.bullets.map((b) => (
                      <ListItem key={b} display="flex" alignItems="flex-start" color="gray.700">
                        <ListIcon as={FiCheck} color="green.500" mt={1} />
                        <Text>{b}</Text>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            ))}
          </VStack>
        </Container>
      </Box>

      {press && (
        <Box as="section" py={{ base: 10, md: 12 }}>
          <Container maxW="900px">
            <Flex direction={{ base: 'column', md: 'row' }} gap={5} align="center" p={6} borderRadius="2xl"
              border="1px solid" borderColor="purple.100" bg="purple.50">
              <Flex boxSize="56px" borderRadius="full" bg="purple.500" color="white" align="center" justify="center" flexShrink={0}>
                <Icon as={FiMic} boxSize={6} />
              </Flex>
              <Box flex="1" textAlign={{ base: 'center', md: 'left' }}>
                <Text fontSize="xs" fontWeight="700" color="purple.600" textTransform="uppercase" letterSpacing="wide">
                  As featured on the {press.outlet}
                </Text>
                <Text fontWeight="700">{press.title}</Text>
                <Text fontSize="sm" color="gray.600">{press.quote} ({press.date})</Text>
              </Box>
              <Button as="a" href={press.url} target="_blank" rel="noopener noreferrer" colorScheme="purple" borderRadius="full" size="sm">
                Listen
              </Button>
            </Flex>
          </Container>
        </Box>
      )}

      <Box as="section" py={{ base: 10, md: 14 }}>
        <Container maxW="760px">
          <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} mb={6} textAlign="center">Frequently asked questions</Heading>
          <Accordion allowMultiple>
            {page.faqs.map((f) => (
              <AccordionItem key={f.q} border="none" mb={3}>
                <AccordionButton bg="gray.50" borderRadius="xl" py={4} px={6} _hover={{ bg: 'gray.100' }} _expanded={{ bg: 'blue.50' }}>
                  <Text as="h3" flex="1" textAlign="left" fontWeight="600">{f.q}</Text>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel py={4} px={6} color="gray.600" fontSize="sm" lineHeight="tall">{f.a}</AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Container>
      </Box>

      {page.related?.length > 0 && (
        <Box as="section" pb={{ base: 10, md: 14 }}>
          <Container maxW="1000px">
            <Heading as="h2" fontSize="lg" mb={4} textAlign="center" color="gray.600">Keep comparing</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              {page.related.map((r) => (
                <Button key={r.slug} as="a" href={`/${r.slug}`} variant="outline" justifyContent="space-between"
                  rightIcon={<FiArrowRight />} whiteSpace="normal" h="auto" py={3} textAlign="left">
                  {r.title}
                </Button>
              ))}
            </SimpleGrid>
          </Container>
        </Box>
      )}

      <Box py={{ base: 14, md: 16 }} bg="blue.600" color="white" textAlign="center">
        <Container maxW="760px">
          <VStack spacing={5}>
            <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }}>Live in under 2 minutes</Heading>
            <Text opacity={0.9}>Free to start, no credit card, no contract. Payments go straight to your own bank.</Text>
            <Button as="a" href="/quickstart" size="lg" bg="white" color="blue.600" borderRadius="full" px={8}
              _hover={{ bg: 'gray.100' }} rightIcon={<FiArrowRight />}>
              Set Up My Laundry Free
            </Button>
            <Text fontSize="sm" opacity={0.85}>
              Questions? <Text as="a" href="mailto:info@smartlaundrybasket.ai" textDecoration="underline">info@smartlaundrybasket.ai</Text>
            </Text>
          </VStack>
        </Container>
      </Box>

      <Box py={6} bg="gray.900" color="gray.400" textAlign="center" fontSize="xs">
        {page.disclaimer && <Text mb={2}>{page.disclaimer}</Text>}
        &copy; {new Date().getFullYear()} Smart Laundry Basket · <Link href="/slb">Home</Link>
        {' · '}<Link href="/privacy-policy">Privacy</Link>{' · '}<Link href="/terms">Terms</Link>
      </Box>
    </Box>
  );
}
